/**
 * @file docGenerationService.ts
 * @description
 * This service handles the generation of PDF and Markdown representations of submission form data.
 *
 * Key responsibilities:
 * - Convert user-entered form data into a readable PDF with a standardized structure.
 * - Convert user-entered form data into a well-formatted Markdown string.
 * - Provide helper functions to handle special formatting, line breaks, etc.
 *
 * @notes
 * - We are using the `jspdf` library for PDF creation in the browser.
 * - For Markdown, we are simply constructing a string with headings, subheadings,
 *   and bullet points. Future expansions might incorporate more advanced formatting.
 */

import { jsPDF } from 'jspdf';

/**
 * Interface for field definitions that map technical field names to human-readable labels
 */
interface FieldDefinition {
  label: string;
  name: string;
  // Optional additional properties that might be useful for formatting
  type?: string;
  section?: string;
}

/**
 * Interface for form sections to preserve display order
 */
interface FormSection {
  heading: string;
  description?: string;
  items: FormField[];
}

interface FormField {
  itemType: 'question' | 'warning' | 'instruction' | 'document';
  name?: string;
  label: string;
  fieldType?: string;
  options?: string[];
  // Other field properties can be added here as needed
}

/**
 * generatePdfFromFormData
 * ------------------------
 * Generates a PDF document (using jsPDF) from the provided form data.
 *
 * @param formData - The final object containing all user-submitted fields.
 * @param formSections - The structured form sections to preserve display order.
 * @param fieldDefinitions - Optional mapping of field names to human-readable labels.
 * @returns A Promise resolving to a Blob object that represents the PDF file.
 *
 * @description
 * We create a base PDF template with sections and fields in the same order as the UI.
 */
export async function generatePdfFromFormData(
  formData: Record<string, any>,
  formSections: FormSection[],
  fieldDefinitions?: FieldDefinition[]
): Promise<Blob> {
  // Create a new jsPDF instance
  const doc = new jsPDF({
    orientation: 'p',
    unit: 'pt',
    format: 'letter',
  });

  // Basic margin and line spacing settings
  const leftMargin = 40; // Current left margin (about 0.56 inches)
  const pageWidth = 612; // Standard letter width in points
  const rightMarginSize = 75; // Increased right margin (about 0.7 inches in points)
  const rightBoundary = pageWidth - rightMarginSize; // Right content boundary
  const contentWidth = rightBoundary - leftMargin; // Available width for content
  let currentY = 40; // vertical position on the PDF
  const lineSpacing = 14;
  const indentSize = 20; // For option indentation

  // Add the Timely IRB Logo as PNG in top-right corner
  // NOTE: You need to add a PNG logo file to your assets
  // The following code assumes the existence of a 'tempus-logo.png' in public/assets
  // If the file doesn't exist, please create it first
  try {
    // Load the PNG logo - you may need to adjust the path based on your project structure
    const logoPath = '/assets/tempus-logo.png';
    // Position the logo in the top-right corner with appropriate margin
    const logoWidth = 120; // Adjust based on your logo size
    const logoHeight = 20; // Adjust based on your logo size
    const logoX = 40;
    const logoY = 25; // Top position

    // Add the logo to the PDF
    doc.addImage(logoPath, 'PNG', logoX, logoY, logoWidth, logoHeight);
  } catch (e) {
    // Fallback to text if image loading fails
    console.error('Failed to load logo image:', e);

    // Add text-based logo as fallback
    const logoColor = '#F55D3E'; // Primary Tomato from guidelines
    doc.setTextColor(logoColor);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text("TIMELY IRB", leftMargin, 35);
  }

  currentY += 50; // Add space after the logo

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor('#343a40'); // Dark Gray from guidelines
  doc.text('Study Submission Form - PDF Version', leftMargin, currentY);
  currentY += 30;

  // Sub-header
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  const submissionName = formData.ref_name || '(Untitled Study)';
  doc.text(`Study ReferenceName: ${submissionName}`, leftMargin, currentY);
  currentY += 20;

  // Additional metadata
  doc.text(`Submitted At: ${new Date().toLocaleString()}`, leftMargin, currentY);
  currentY += 30;

  // Create a mapping of field names to labels if fieldDefinitions is provided
  const fieldLabels: Record<string, string> = {};
  if (fieldDefinitions) {
    fieldDefinitions.forEach(field => {
      fieldLabels[field.name] = field.label;
    });
  }

  // Function to ensure wrapping text properly with strict margin enforcement
  const wrapAndPrintText = (text: string, x: number, y: number, maxWidth: number, isBold: boolean = false) => {
    // Handle the case where text is already wrapped by HTML tags
    text = text.replace(/<br\s*\/?>/gi, '\n'); // Convert <br> tags to newlines
    text = text.replace(/<[^>]*>/g, ' ').trim(); // Remove other HTML tags and replace with spaces

    // Ensure maxWidth never exceeds content boundaries
    const safeMaxWidth = Math.min(maxWidth, rightBoundary - x);

    // Split text into lines that respect the safe max width
    const lines = doc.splitTextToSize(text, safeMaxWidth);

    if (isBold) {
      doc.setFont('helvetica', 'bold');
    } else {
      doc.setFont('helvetica', 'normal');
    }

    let yPos = y;
    for (const line of lines) {
      // Check if we need a new page
      if (yPos > 750) {
        doc.addPage();
        yPos = 40;
      }

      doc.text(line, x, yPos);
      yPos += lineSpacing;
    }

    return yPos;
  };

  // Iterate through sections and fields in order
  for (const section of formSections) {
    // Check if we need a new page for this section
    if (currentY > 750) {
      doc.addPage();
      currentY = 40;
    }

    // Add section heading
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    currentY = wrapAndPrintText(section.heading, leftMargin, currentY, contentWidth, true);
    currentY += 10;

    // Add section description if available
    if (section.description) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      currentY = wrapAndPrintText(section.description, leftMargin, currentY, contentWidth);
      currentY += 6; // Extra space after description
    }

    // Process fields in this section
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);

    for (const field of section.items) {
      // Skip non-question items or fields without names
      if ((field.itemType !== 'question' && field.itemType !== 'document') || !field.name) {
        continue;
      }

      const fieldName = field.name;
      const fieldValue = formData[fieldName];

      // Skip empty fields
      if (fieldValue === undefined || fieldValue === null) {
        continue;
      }

      // Add spacing between fields
      currentY += 5;

      // Handle different field types
      if (field.fieldType === 'radio' || field.fieldType === 'dropdown-single' ||
          field.fieldType === 'checkboxes' || field.fieldType === 'dropdown-multiple') {

        // Check if we need a new page
        if (currentY > 750) {
          doc.addPage();
          currentY = 40;
        }

        // Use the human-readable label if available
        const displayLabel = field.label || fieldLabels[fieldName] || fieldName;

        // Print the field label with proper wrapping
        currentY = wrapAndPrintText(displayLabel, leftMargin, currentY, contentWidth, true);

        // For multi-select options (checkboxes, dropdown-multiple)
        if (field.fieldType === 'checkboxes' || field.fieldType === 'dropdown-multiple') {
          const selectedValues = Array.isArray(fieldValue) ? fieldValue : [fieldValue];

          if (field.options) {
            for (const option of field.options) {
              // Determine if this option is selected
              const isSelected = selectedValues.includes(option);

              // Use standard ASCII characters instead of Unicode checkboxes
              const checkbox = isSelected ? "[X]" : "[ ]";

              // Calculate space for option text after checkbox, ensuring it respects the right margin
              const optionTextWidth = Math.min(contentWidth - indentSize - 20, rightBoundary - (leftMargin + indentSize + 20)); // 20 is space for checkbox

              // Print the option with checkbox and proper wrapping
              currentY = wrapAndPrintText(`${checkbox} ${option}`, leftMargin + indentSize, currentY, optionTextWidth);
            }
          }
        }
        // For single-select options (radio, dropdown-single)
        else {
          if (field.options) {
            for (const option of field.options) {
              // Determine if this option is selected
              const isSelected = fieldValue === option;

              // Use standard ASCII characters instead of Unicode checkboxes
              const checkbox = isSelected ? "[X]" : "[ ]";

              // Calculate space for option text after checkbox, ensuring it respects the right margin
              const optionTextWidth = Math.min(contentWidth - indentSize - 20, rightBoundary - (leftMargin + indentSize + 20)); // 20 is space for checkbox

              // Print the option with checkbox and proper wrapping
              currentY = wrapAndPrintText(`${checkbox} ${option}`, leftMargin + indentSize, currentY, optionTextWidth);
            }
          }
        }
      }
      // Handle regular text/textarea fields
      else if (typeof fieldValue === 'string' || typeof fieldValue === 'number') {
        // Check if we need a new page
        if (currentY > 750) {
          doc.addPage();
          currentY = 40;
        }

        // Use the human-readable label if available
        const displayLabel = field.label || fieldLabels[fieldName] || fieldName;

        // Print the label with proper wrapping
        currentY = wrapAndPrintText(displayLabel, leftMargin, currentY, contentWidth, true);

        // Print the value with proper wrapping and indentation
        currentY = wrapAndPrintText(String(fieldValue), leftMargin + indentSize, currentY, contentWidth - indentSize);
      }

      // Handle document fields
      else if (field.itemType === 'document' && typeof fieldValue === 'object') {
        // Check if we need a new page
        if (currentY > 750) {
          doc.addPage();
          currentY = 40;
        }

        // Use the human-readable label if available
        const displayLabel = field.label || fieldLabels[fieldName] || fieldName;

        // Print the label with proper wrapping
        currentY = wrapAndPrintText(displayLabel, leftMargin, currentY, contentWidth, true);

        // Handle both single document and multiple documents
        if (Array.isArray(fieldValue)) {
          // Multiple documents
          for (const doc of fieldValue) {
            if (doc && (doc.filename || doc.name)) {
              const filename = doc.filename || doc.name || 'Not uploaded';
              currentY = wrapAndPrintText(`- ${filename}`, leftMargin + indentSize, currentY, contentWidth - indentSize);
            }
          }
        } else if (fieldValue) {
          // Single document
          const filename = fieldValue.filename || fieldValue.name || 'Not uploaded';
          currentY = wrapAndPrintText(`- ${filename}`, leftMargin + indentSize, currentY, contentWidth - indentSize);
        }
      }

      // Skip complex objects
      else if (typeof fieldValue === 'object' || typeof fieldValue === 'function') {
        continue;
      }

      // Add space between fields
      currentY += 8;
    }

    // Add space after each section
    currentY += 16;
  }

  // Add a final note
  if (currentY > 750) {
    doc.addPage();
    currentY = 40;
  }

  doc.setFontSize(10);
  doc.setTextColor('#444444');
  doc.text(
    'This PDF was generated automatically by Timely IRB. ' +
    'It is not an official IRB decision letter.',
    leftMargin,
    currentY
  );

  // Return a Blob from the PDF doc output
  const pdfBlob = doc.output('blob');
  return pdfBlob;
}

/**
 * generateMarkdownFromFormData
 * ----------------------------
 * Produces a Markdown string summarizing the submission's data.
 *
 * @param formData - The final object containing all user-submitted fields.
 * @param formSections - The structured form sections to preserve display order.
 * @param fieldDefinitions - Optional mapping of field names to human-readable labels.
 * @returns A Promise resolving to a string of Markdown.
 */
export async function generateMarkdownFromFormData(
  formData: Record<string, any>,
  formSections: FormSection[],
  fieldDefinitions?: FieldDefinition[]
): Promise<string> {
  let md = `# Timely IRB Submission - Markdown Export\n\n`;

  const submissionName = formData.ref_name || '(Untitled Study)';
  md += `**Study Name**: ${submissionName}\n\n`;
  md += `**Generated At**: ${new Date().toLocaleString()}\n\n`;

  // Create a mapping of field names to labels if fieldDefinitions is provided
  const fieldLabels: Record<string, string> = {};
  if (fieldDefinitions) {
    fieldDefinitions.forEach(field => {
      fieldLabels[field.name] = field.label;
    });
  }

  // Process sections in order
  for (const section of formSections) {
    md += `## ${section.heading}\n\n`;

    // Add section description if available
    if (section.description) {
      md += `${section.description}\n\n`;
    }

    // Process fields in this section
    for (const field of section.items) {
      // Skip non-question items or fields without names
      if ((field.itemType !== 'question' && field.itemType !== 'document') || !field.name) {
        continue;
      }

      const fieldName = field.name;
      const fieldValue = formData[fieldName];

      // Skip empty fields
      if (fieldValue === undefined || fieldValue === null) {
        continue;
      }

      // Handle different field types
      if (field.fieldType === 'radio' || field.fieldType === 'dropdown-single' ||
          field.fieldType === 'checkboxes' || field.fieldType === 'dropdown-multiple') {

        // Use the human-readable label if available
        const displayLabel = field.label || fieldLabels[fieldName] || fieldName;

        // Remove HTML tags from the label for cleaner display
        const cleanLabel = displayLabel.replace(/<[^>]*>/g, ' ').trim();

        // Print the field label
        md += `### ${cleanLabel}\n\n`;

        // For multi-select options (checkboxes, dropdown-multiple)
        if (field.fieldType === 'checkboxes' || field.fieldType === 'dropdown-multiple') {
          const selectedValues = Array.isArray(fieldValue) ? fieldValue : [fieldValue];

          if (field.options) {
            for (const option of field.options) {
              // Clean option text from HTML tags
              const cleanOption = option.replace(/<[^>]*>/g, '');

              // Determine if this option is selected
              const isSelected = selectedValues.includes(option);

              // Use standard ASCII characters instead of Unicode checkboxes
              const checkbox = isSelected ? "[X]" : "[ ]";

              // Add the option with checkbox
              md += `- ${checkbox} ${cleanOption}\n`;
            }
            md += '\n';
          }
        }
        // For single-select options (radio, dropdown-single)
        else {
          if (field.options) {
            for (const option of field.options) {
              // Clean option text from HTML tags
              const cleanOption = option.replace(/<[^>]*>/g, '');

              // Determine if this option is selected
              const isSelected = fieldValue === option;

              // Use standard ASCII characters instead of Unicode checkboxes
              const checkbox = isSelected ? "[X]" : "[ ]";

              // Add the option with checkbox
              md += `- ${checkbox} ${cleanOption}\n`;
            }
            md += '\n';
          }
        }
      }
      // Handle regular text/textarea fields
      else if (typeof fieldValue === 'string' || typeof fieldValue === 'number') {
        // Use the human-readable label if available
        const displayLabel = field.label || fieldLabels[fieldName] || fieldName;

        // Remove HTML tags from the label
        const cleanLabel = displayLabel.replace(/<[^>]*>/g, '');

        // Add the label and value
        md += `### ${cleanLabel}\n\n`;
        md += `${fieldValue}\n\n`;
      }

      // Handle document fields
      else if (field.itemType === 'document' && typeof fieldValue === 'object') {
        // Use the human-readable label if available
        const displayLabel = field.label || fieldLabels[fieldName] || fieldName;

        // Remove HTML tags from the label
        const cleanLabel = displayLabel.replace(/<[^>]*>/g, '');

        // Add the label
        md += `### ${cleanLabel}\n\n`;

        // Handle both single document and multiple documents
        if (Array.isArray(fieldValue)) {
          // Multiple documents
          for (const doc of fieldValue) {
            if (doc && (doc.filename || doc.name)) {
              const filename = doc.filename || doc.name || 'Not uploaded';
              md += `- ${filename}\n`;
            }
          }
          md += '\n';
        } else if (fieldValue) {
          // Single document
          const filename = fieldValue.filename || fieldValue.name || 'Not uploaded';
          md += `- ${filename}\n\n`;
        }
      }

      // Skip complex objects
      else if (typeof fieldValue === 'object' || typeof fieldValue === 'function') {
        continue;
      }
    }
  }

  md += `\n> This Markdown was generated automatically by Timely IRB. It is not an official IRB decision letter.\n`;

  return md;
}
